import { Container, type Sprite } from 'pixi.js';
import type { MeteorsEffect, ShootingStarAttack } from '../../data/full/celeste';
import { clamp01, frac, lerp, smoothstep } from '../../shared/scalar';
import {
  SHOOTING_STAR,
  crossing,
  linkReach,
  starLinks,
  starsOf,
} from '../../sim/full/attacks/shooting-star';
import { METEOR, PORTAL } from '../../sim/full/skills/meteors';
import type { PlayerState, PropState, SimState, ZoneState } from '../../sim/state';
import { createBurster } from '../class-bursts';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Shape, type Sheet, paintSheet, paintedLater } from '../paint';
import { add, byId, hide, lookup, setTint } from '../util';
import { ViewPool } from '../views';
import {
  DOT,
  LINE,
  METEOR_TAIL,
  PORTAL as PORTAL_SHAPE,
  SKY_PIECES,
  SPARKLE,
  STAR_TINTS,
} from './celeste-paint';

type SkyShapes = Readonly<Record<keyof typeof SKY_PIECES, Shape>>;
const HUES = [0, 1, 2, 3] as const;
type Hue = (typeof HUES)[number];

function hueAt(count: number): Hue {
  return HUES[count % HUES.length] ?? 0;
}

const STAR = {
  radius: 12,
  glow: 26,
  glowAlpha: 0.35,
  beatAlpha: 0.35,
  dots: 3,
  dot: 1.1,
  drift: 8,
  driftMore: 10,
  driftBeats: 0.7,
} as const;
const SPARK = STAR.radius / 10;
const FADE_TICKS = 8;
const SHOOTING = { size: 0.78, twinkle: 4 } as const;
const THREAD = {
  ribbon: 7,
  thread: 1.5,
  ribbonAlpha: 0.45,
  beatAlpha: 0.35,
  runner: 2.2,
  runnerAlpha: 0.9,
  runnerBeats: 1.25,
  grow: 0.2,
} as const;
const BOOST = { ticks: 7, from: 4, to: 18, peak: 0.9 };
const SKY = { lift: 90, fadeIn: 0.4, fadeOut: 0.6, spin: 0.9, calmSpin: 0.2, glow: 80 };
const MARK = { alpha: 0.3, calmAlpha: 0.2 };
const PORTAL_VIOLET = 0xa98cff;
const FALL = { glow: 26 };
const BLAST = { beats: 0.6, from: 8, glow: 0.5, inner: 0.6, innerAlpha: 0.7 };

export function twinkle(beats: number, index: number): number {
  return 1 + 0.35 * Math.exp(-4 * frac(beats + index * 0.13));
}

export function popOf(age: number): number {
  if (age < 0) {
    return 0;
  }
  if (age < 0.25) {
    const share = age / 0.25;
    return smoothstep(share) * 1.35;
  }
  return 1 + 0.35 * Math.exp(-9 * (age - 0.25)) * Math.cos((age - 0.25) * 18);
}

interface StarView {
  readonly glow: Sprite;
  readonly points: Sprite;
  readonly heart: Sprite;
  readonly dots: readonly Sprite[];
}

interface LineView {
  readonly ribbon: Sprite;
  readonly thread: Sprite;
  readonly runner: Sprite;
}

interface PortalView {
  readonly glow: Sprite;
  readonly mark: Sprite;
  readonly rings: Sprite;
  readonly inner: Sprite;
  // The spiral turns flat in the plane of the portal: it turns inside a squashed container.
  readonly plane: Container;
  readonly spiral: Sprite;
}

interface FallView {
  readonly glow: Sprite;
  readonly body: Sprite;
}

interface Seen {
  x: number;
  y: number;
  playerId: number;
  seen: number;
  born: number;
  gone?: number;
}

export function createConstellations(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const sheet = paintedLater((): Sheet<keyof typeof SKY_PIECES> => paintSheet(SKY_PIECES));
  const burster = createBurster(ctx, 64);
  const attacks = new Map<string, ShootingStarAttack>();
  for (const [classId, attack] of ctx.attacks) {
    if (attack.kind === 'shootingStar') {
      attacks.set(classId, attack);
    }
  }
  const skills = new Map<string, MeteorsEffect>();
  for (const [classId, effect] of ctx.skillEffects) {
    if (effect.kind === 'meteors') {
      skills.set(classId, effect);
    }
  }
  const starBorn = new Map<number, number>();
  const starHue = new Map<number, Hue>();
  const laid = new Map<number, number>();
  const lineBorn = new Map<number, number>();
  const boosted = new Map<string, number>();
  const portals = new Map<number, Seen>();
  const meteors = new Map<number, Seen>();
  const lineLayer = new Container();
  const starLayer = new Container();
  layers.fx.addChild(lineLayer, starLayer);
  let stars: ViewPool<StarView> | undefined;
  let flying: ViewPool<StarView> | undefined;
  let lines: ViewPool<LineView> | undefined;
  let gates: ViewPool<PortalView> | undefined;
  let falls: ViewPool<FallView> | undefined;
  let image = 0;

  function white(frame: Frame): number {
    return frame.light.additive ? 0xffffff : frame.palette.texte;
  }

  function colorOf(owner: PlayerState, frame: Frame): number {
    return frame.palette[lookup(ctx.classTokens, owner.classId, 'class')];
  }

  function beatsOf(frame: Frame): number {
    return (frame.now - frame.tempo.originTick) / frame.tempo.ticksPerBeat;
  }

  function starPool(shapes: SkyShapes, parent: Container, dots: number): ViewPool<StarView> {
    return new ViewPool<StarView>(
      () => ({
        glow: add(layers.glow, textures.halo),
        points: add(parent, shapes.points0),
        heart: add(parent, shapes.heart0),
        dots: Array.from({ length: dots }, () => add(parent, shapes.dot)),
      }),
      (view) => {
        hide(view.glow, view.points, view.heart, ...view.dots);
      },
    );
  }

  function placeStar(
    view: StarView,
    x: number,
    y: number,
    hue: Hue,
    size: number,
    reach: number,
    shown: number,
    frame: Frame,
    shapes: SkyShapes,
  ): void {
    const points = ([shapes.points0, shapes.points1, shapes.points2, shapes.points3] as const)[hue];
    const heart = ([shapes.heart0, shapes.heart1, shapes.heart2, shapes.heart3] as const)[hue];
    view.points.visible = view.heart.visible = view.glow.visible = true;
    view.points.texture = points.texture;
    view.points.position.set(x, y);
    view.points.scale.set((SPARKLE.points * SPARK * size * reach) / points.radius);
    view.points.alpha = shown;
    view.heart.texture = heart.texture;
    view.heart.position.set(x, y);
    view.heart.scale.set((SPARKLE.small * SPARK * size) / heart.radius);
    view.heart.alpha = shown;
    view.glow.position.set(x, y);
    view.glow.scale.set((STAR.glow * SPARK * size) / textures.halo.radius);
    view.glow.alpha = Math.min(
      1,
      (STAR.glowAlpha + STAR.beatAlpha * frame.pulse) * shown * frame.light.haloAlpha,
    );
    setTint(view.glow, STAR_TINTS[hue] ?? 0xffffff);
  }

  function drift(view: StarView, x: number, y: number, hue: Hue, shown: number, frame: Frame) {
    const beats = beatsOf(frame);
    view.dots.forEach((dot, index) => {
      dot.visible = !frame.calm;
      if (frame.calm) {
        return;
      }
      const share = frac(beats * STAR.driftBeats + index / STAR.dots + hue * 0.21);
      const angle = index * 2.1 + hue;
      const out = (STAR.drift + STAR.driftMore * share) * SPARK;
      dot.position.set(x + Math.cos(angle) * out, y + Math.sin(angle) * out);
      dot.scale.set((STAR.dot * SPARK) / DOT);
      dot.alpha = Math.sin(share * Math.PI) * 0.9 * shown;
      setTint(dot, white(frame));
    });
  }

  function drawFlying(state: SimState, alpha: number, frame: Frame, shapes: SkyShapes): void {
    flying ??= starPool(shapes, layers.shots, 0);
    flying.begin();
    const beats = beatsOf(frame);
    const inFlight = new Map<number, number>();
    for (const prop of state.props ?? []) {
      const owner = prop.kind === SHOOTING_STAR ? byId(state.players, prop.playerId) : undefined;
      if (owner === undefined) {
        continue;
      }
      const ahead = inFlight.get(owner.id) ?? 0;
      inFlight.set(owner.id, ahead + 1);
      const hue = hueAt((laid.get(owner.id) ?? 0) + ahead);
      const x = lerp(prop.prevX, prop.x, alpha);
      const y = lerp(prop.prevY, prop.y, alpha);
      const reach = frame.calm ? 1 : twinkle(beats * SHOOTING.twinkle, hue);
      placeStar(flying.acquire(prop.id), x, y, hue, SHOOTING.size, reach, 1, frame, shapes);
    }
    flying.end();
  }

  function hueOf(zone: ZoneState, owner: PlayerState): Hue {
    let hue = starHue.get(zone.id);
    if (hue === undefined) {
      const count = laid.get(owner.id) ?? 0;
      hue = hueAt(count);
      laid.set(owner.id, count + 1);
      starHue.set(zone.id, hue);
    }
    return hue;
  }

  function drawStars(
    state: SimState,
    owner: PlayerState,
    attack: ShootingStarAttack,
    alpha: number,
    frame: Frame,
    shapes: SkyShapes,
  ): ZoneState[] {
    stars ??= starPool(shapes, starLayer, STAR.dots);
    const { now, tempo, calm } = frame;
    const beats = beatsOf(frame);
    const mine = starsOf(state.zones, owner, attack);
    for (const zone of mine) {
      if (!starBorn.has(zone.id)) {
        starBorn.set(zone.id, now);
      }
      const since = now - (starBorn.get(zone.id) ?? now);
      const hue = hueOf(zone, owner);
      const pop = calm ? 1 : popOf(since / tempo.ticksPerBeat);
      const shown = clamp01(Math.min((zone.ticksLeft - alpha) / FADE_TICKS, since / 2));
      const reach = calm ? 1 : twinkle(beats, hue);
      const view = stars.acquire(zone.id);
      placeStar(view, zone.x, zone.y, hue, pop, reach, shown, frame, shapes);
      drift(view, zone.x, zone.y, hue, shown, frame);
    }
    return mine;
  }

  function drawLines(
    owner: PlayerState,
    attack: ShootingStarAttack,
    mine: readonly ZoneState[],
    alpha: number,
    frame: Frame,
    shapes: SkyShapes,
  ): void {
    lines ??= new ViewPool<LineView>(
      () => ({
        ribbon: add(lineLayer, shapes.ribbon),
        thread: add(lineLayer, shapes.thread),
        runner: add(lineLayer, shapes.dot),
      }),
      (view) => {
        hide(view.ribbon, view.thread, view.runner);
      },
    );
    const { now, tempo, pulse, calm } = frame;
    const beats = beatsOf(frame);
    for (const [i, j] of starLinks(mine, linkReach(owner, attack))) {
      const a = mine[i];
      const b = mine[j];
      if (a === undefined || b === undefined) {
        continue;
      }
      const key = a.id * 2 ** 21 + b.id;
      if (!lineBorn.has(key)) {
        lineBorn.set(key, now);
      }
      const grow = calm
        ? 1
        : Math.min(1, (now - (lineBorn.get(key) ?? now)) / (THREAD.grow * tempo.ticksPerBeat));
      const shown = clamp01(
        Math.min((a.ticksLeft - alpha) / FADE_TICKS, (b.ticksLeft - alpha) / FADE_TICKS),
      );
      const dx = (b.x - a.x) * grow;
      const dy = (b.y - a.y) * grow;
      const length = Math.hypot(dx, dy);
      const rotation = Math.atan2(dy, dx);
      const view = lines.acquire(key);
      for (const [sprite, height, drawn, tint, strength] of [
        [
          view.ribbon,
          THREAD.ribbon,
          LINE.ribbon,
          0xffffff,
          THREAD.ribbonAlpha + THREAD.beatAlpha * pulse,
        ],
        [view.thread, THREAD.thread, LINE.thread, white(frame), 1],
      ] as const) {
        sprite.visible = true;
        sprite.position.set(a.x + dx / 2, a.y + dy / 2);
        sprite.rotation = rotation;
        sprite.scale.set(length / LINE.length, height / drawn);
        sprite.alpha = Math.min(1, strength * shown);
        setTint(sprite, tint);
      }
      view.runner.visible = !calm;
      const run = frac(beats / THREAD.runnerBeats + (a.id % 7) / 7);
      view.runner.position.set(a.x + dx * run, a.y + dy * run);
      view.runner.scale.set(THREAD.runner / DOT);
      view.runner.alpha = THREAD.runnerAlpha * shown;
      setTint(view.runner, white(frame));
    }
  }

  function flashBoosts(
    state: SimState,
    owner: PlayerState,
    mine: readonly ZoneState[],
    attack: ShootingStarAttack,
    frame: Frame,
  ): void {
    const links = starLinks(mine, linkReach(owner, attack));
    if (links.length === 0) {
      return;
    }
    const color = colorOf(owner, frame);
    const flash = (id: number, x: number, y: number, vx: number, vy: number): void => {
      for (const [i, j] of links) {
        const a = mine[i];
        const b = mine[j];
        if (a === undefined || b === undefined) {
          continue;
        }
        const share = crossing({ x: x - vx, y: y - vy }, { x: vx, y: vy }, a, b);
        const key = `${String(id)}:${String(a.id)}:${String(b.id)}`;
        if (share === undefined || boosted.has(key)) {
          continue;
        }
        boosted.set(key, frame.now);
        burster.spawn(frame, {
          shape: textures.ring,
          now: frame.now,
          duration: BOOST.ticks,
          x: x - vx + vx * share,
          y: y - vy + vy * share,
          fromRadius: BOOST.from,
          toRadius: BOOST.to,
          tint: color,
          peak: BOOST.peak,
        });
      }
    };
    for (const shot of state.projectiles) {
      if (shot.owner.kind !== 'enemy' && shot.arc === undefined && shot.returning !== true) {
        flash(shot.id, shot.x, shot.y, shot.x - shot.prevX, shot.y - shot.prevY);
      }
    }
    for (const prop of state.props ?? []) {
      if (prop.kind === SHOOTING_STAR) {
        flash(prop.id, prop.x, prop.y, prop.x - prop.prevX, prop.y - prop.prevY);
      }
    }
  }

  function portalPool(shapes: SkyShapes): ViewPool<PortalView> {
    gates ??= new ViewPool<PortalView>(
      () => {
        const plane = new Container();
        layers.fx.addChild(plane);
        plane.scale.set(1, PORTAL_SHAPE.height / PORTAL_SHAPE.width);
        return {
          glow: add(layers.glow, textures.halo),
          mark: add(layers.zones, textures.ring),
          rings: add(layers.fx, shapes.portal),
          inner: add(layers.fx, shapes.portal),
          plane,
          spiral: add(plane, shapes.portalSpiral),
        };
      },
      (view) => {
        hide(view.glow, view.mark, view.rings, view.inner);
        view.plane.visible = false;
      },
    );
    return gates;
  }

  function drawPortal(
    id: number,
    seen: Seen,
    owner: PlayerState,
    effect: MeteorsEffect,
    frame: Frame,
    shapes: SkyShapes,
    shown: number,
  ): void {
    const view = portalPool(shapes).acquire(id);
    const { now, tempo, calm, light } = frame;
    const color = colorOf(owner, frame);
    const x = seen.x;
    const y = seen.y - SKY.lift;
    view.glow.visible = view.mark.visible = view.rings.visible = true;
    view.inner.visible = view.plane.visible = true;
    view.glow.position.set(x, y);
    view.glow.scale.set(SKY.glow / textures.halo.radius);
    view.glow.alpha = 0.45 * shown * light.haloAlpha;
    setTint(view.glow, color);
    view.rings.position.set(x, y);
    view.rings.alpha = shown;
    setTint(view.rings, color);
    view.inner.position.set(x, y);
    view.inner.scale.set(0.62);
    view.inner.alpha = 0.8 * shown;
    setTint(view.inner, PORTAL_VIOLET);
    view.plane.position.set(x, y);
    view.spiral.rotation =
      -((now - tempo.originTick) / tempo.ticksPerBeat) * (calm ? SKY.calmSpin : SKY.spin);
    view.spiral.alpha = shown;
    setTint(view.spiral, white(frame));
    view.mark.position.set(seen.x, seen.y);
    view.mark.scale.set(effect.radius / textures.ring.radius);
    view.mark.alpha = shown * (calm ? MARK.calmAlpha : MARK.alpha);
    setTint(view.mark, color);
  }

  function drawFalling(
    prop: PropState,
    effect: MeteorsEffect,
    alpha: number,
    frame: Frame,
    shapes: SkyShapes,
  ): void {
    falls ??= new ViewPool<FallView>(
      () => ({ glow: add(layers.glow, textures.halo), body: add(layers.fx, shapes.meteor) }),
      (view) => {
        hide(view.glow, view.body);
      },
    );
    const view = falls.acquire(prop.id);
    const left = Math.max(0, prop.ticksLeft - alpha) / effect.fallTicks;
    const x = lerp(prop.prevX, prop.x, alpha);
    const y = lerp(prop.prevY, prop.y, alpha) - SKY.lift * left;
    const heading = Math.atan2(prop.vy + SKY.lift / effect.fallTicks, prop.vx);
    const { palette, light } = frame;
    view.body.visible = view.glow.visible = true;
    view.body.position.set(
      x - (Math.cos(heading) * METEOR_TAIL.length) / 2 + Math.cos(heading) * METEOR_TAIL.head,
      y - (Math.sin(heading) * METEOR_TAIL.length) / 2 + Math.sin(heading) * METEOR_TAIL.head,
    );
    view.body.rotation = heading;
    setTint(view.body, palette.noyau);
    view.glow.position.set(x, y);
    view.glow.scale.set(FALL.glow / textures.halo.radius);
    view.glow.alpha = 0.9 * light.haloAlpha;
    setTint(view.glow, palette.noyau);
  }

  function blast(seen: Seen, effect: MeteorsEffect, frame: Frame): void {
    const duration = BLAST.beats * frame.tempo.ticksPerBeat;
    const at = { now: frame.now, duration, x: seen.x, y: seen.y };
    burster.spawn(frame, {
      ...at,
      shape: textures.halo,
      fromRadius: effect.radius,
      toRadius: effect.radius,
      tint: frame.palette.noyau,
      peak: BLAST.glow * frame.light.haloAlpha,
    });
    burster.spawn(frame, {
      ...at,
      shape: textures.ring,
      fromRadius: BLAST.from,
      toRadius: effect.radius,
      tint: frame.palette.noyau,
      peak: 1,
      outline: true,
    });
    burster.spawn(frame, {
      ...at,
      shape: textures.ring,
      fromRadius: BLAST.from / 2,
      toRadius: effect.radius * BLAST.inner,
      tint: white(frame),
      peak: BLAST.innerAlpha,
    });
  }

  function drawSky(state: SimState, alpha: number, frame: Frame, shapes: SkyShapes): void {
    gates?.begin();
    falls?.begin();
    const fadeIn = SKY.fadeIn * frame.tempo.ticksPerBeat;
    const fadeOut = SKY.fadeOut * frame.tempo.ticksPerBeat;
    for (const prop of state.props ?? []) {
      if (prop.kind !== PORTAL && prop.kind !== METEOR) {
        continue;
      }
      const owner = byId(state.players, prop.playerId);
      const effect = owner === undefined ? undefined : skills.get(owner.classId);
      if (owner === undefined || effect === undefined) {
        continue;
      }
      const tracks = prop.kind === PORTAL ? portals : meteors;
      const seen = tracks.get(prop.id) ?? {
        x: prop.x,
        y: prop.y,
        playerId: owner.id,
        seen: image,
        born: frame.now,
      };
      seen.x = prop.x;
      seen.y = prop.y;
      seen.seen = image;
      tracks.set(prop.id, seen);
      if (prop.kind === PORTAL) {
        const shown = Math.min(1, (frame.now - seen.born) / fadeIn);
        drawPortal(prop.id, seen, owner, effect, frame, shapes, shown);
      } else {
        drawFalling(prop, effect, alpha, frame, shapes);
      }
    }
    for (const [id, seen] of meteors) {
      if (seen.seen === image) {
        continue;
      }
      meteors.delete(id);
      const owner = byId(state.players, seen.playerId);
      const effect = owner === undefined ? undefined : skills.get(owner.classId);
      if (effect !== undefined) {
        blast(seen, effect, frame);
      }
    }
    for (const [id, seen] of portals) {
      if (seen.seen === image) {
        continue;
      }
      seen.gone ??= frame.now;
      const owner = byId(state.players, seen.playerId);
      const effect = owner === undefined ? undefined : skills.get(owner.classId);
      const closing = 1 - (frame.now - seen.gone) / fadeOut;
      if (owner === undefined || effect === undefined || closing <= 0) {
        portals.delete(id);
        continue;
      }
      drawPortal(id, seen, owner, effect, frame, shapes, closing);
    }
    gates?.end();
    falls?.end();
  }

  return {
    reset(): void {
      starBorn.clear();
      starHue.clear();
      laid.clear();
      lineBorn.clear();
      boosted.clear();
      portals.clear();
      meteors.clear();
      burster.bursts.clear();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      if (attacks.size === 0 && skills.size === 0) {
        return;
      }
      image += 1;
      const { shapes } = sheet.get();
      drawFlying(state, alpha, frame, shapes);
      stars?.begin();
      lines?.begin();
      for (const owner of state.players) {
        const attack = attacks.get(owner.classId);
        if (attack === undefined) {
          continue;
        }
        const mine = drawStars(state, owner, attack, alpha, frame, shapes);
        drawLines(owner, attack, mine, alpha, frame, shapes);
        flashBoosts(state, owner, mine, attack, frame);
      }
      stars?.end();
      lines?.end();
      drawSky(state, alpha, frame, shapes);
      for (const id of starBorn.keys()) {
        if (stars?.peek(id) === undefined) {
          starBorn.delete(id);
          starHue.delete(id);
        }
      }
      for (const id of lineBorn.keys()) {
        if (lines?.peek(id) === undefined) {
          lineBorn.delete(id);
        }
      }
      for (const [key, at] of boosted) {
        if (frame.now - at > 2 * frame.tempo.ticksPerBeat) {
          boosted.delete(key);
        }
      }
      burster.bursts.update(state, alpha, frame);
    },
    destroy(): void {
      burster.bursts.clear();
      lineLayer.destroy({ children: true });
      starLayer.destroy({ children: true });
      sheet.painted?.source.destroy();
    },
  };
}
